/**
 * Zone de dépôt de fichiers.
 *
 * Trois façons d'ajouter des fichiers, parce que c'est le premier point de friction :
 * le sélecteur, le glisser-déposer et le collage. Cette dernière est particulièrement
 * utile sur ordinateur quand on vient de faire une capture d'écran.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useT } from '../i18n/index.js';

interface FileDropProps {
  onFiles: (files: File[]) => void;
  disabled?: boolean;
}

export function FileDrop({ onFiles, disabled = false }: FileDropProps): React.JSX.Element {
  const [dragging, setDragging] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const translator = useT();
  // Compteur plutôt que booléen : `dragleave` se déclenche aussi en passant sur un enfant,
  // ce qui ferait clignoter l'état avec un simple booléen.
  const dragDepth = useRef(0);

  const handlePaste = useCallback(
    (event: ClipboardEvent): void => {
      if (disabled) return;
      const files = Array.from(event.clipboardData?.files ?? []);
      if (files.length > 0) onFiles(files);
    },
    [disabled, onFiles],
  );

  useEffect(() => {
    window.addEventListener('paste', handlePaste);
    return () => window.removeEventListener('paste', handlePaste);
  }, [handlePaste]);

  return (
    <div className="stack">
      <button
        type="button"
        className="dropzone"
        data-dragging={dragging}
        disabled={disabled}
        onClick={() => inputRef.current?.click()}
        onDragEnter={(event) => {
          event.preventDefault();
          dragDepth.current += 1;
          setDragging(true);
        }}
        onDragOver={(event) => {
          // Sans `preventDefault`, le navigateur ouvre le fichier au lieu de le déposer.
          event.preventDefault();
        }}
        onDragLeave={(event) => {
          event.preventDefault();
          dragDepth.current = Math.max(0, dragDepth.current - 1);
          if (dragDepth.current === 0) setDragging(false);
        }}
        onDrop={(event) => {
          event.preventDefault();
          dragDepth.current = 0;
          setDragging(false);
          const files = Array.from(event.dataTransfer.files);
          if (files.length > 0) onFiles(files);
        }}
      >
        <span className="dropzone-icon" aria-hidden="true">
          ↑
        </span>
        <span className="dropzone-title">{translator.t('drop.title')}</span>
        <span className="dropzone-hint">{translator.t('drop.hint')}</span>
      </button>

      <input
        ref={inputRef}
        type="file"
        multiple
        className="sr-only"
        tabIndex={-1}
        aria-hidden="true"
        onChange={(event) => {
          const files = Array.from(event.target.files ?? []);
          if (files.length > 0) onFiles(files);
          // Réinitialiser permet de resélectionner le même fichier juste après.
          event.target.value = '';
        }}
      />
    </div>
  );
}
